import React from "react";

import { cn } from "@workspace/web-ui/lib/utils";
import { PatientTimeline } from "../../api/types";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Image } from "@workspace/web-ui/components/image";
import { formatToMonthDayYear, formatTime } from "@workspace/lib/date";


interface TimelineProps {
    timelineData?: {
        status: string;
        message: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        timeline: Array<any>;
        patient_summary: {
            full_name: string;
            age: number;
            gender: string;
        };
    };
}

export const Timeline = ({ timelineData }: TimelineProps) => {
    const defaultTimeline = {
        status: '',
        message: '',
        timeline: [],
        patient_summary: {
            full_name: '',
            age: 0,
            gender: ''
        }
    };

    return <TimelineCard patient={timelineData ?? defaultTimeline} />
}

interface TimelineDetailsProps {
    title: string;
    paragraph: string;
    encounter_datetime: string;
}

const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: "", time: "" };

    try {
        // Parse the ISO datetime string
        const dateObj = new Date(dateTimeString);

        // Format date as YYYY-MM-DD
        const date = dateObj.toISOString().split('T')[0];

        // Format time as HH:MM
        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const time = `${hours}:${minutes}`;

        return { date, time };
    } catch (error) {
        console.error("Error parsing datetime:", error);
        return { date: "", time: "" };
    }
};

const TimelineDetails = ({ title, paragraph, encounter_datetime }: TimelineDetailsProps) => {
    const { date, time } = formatDateTime(encounter_datetime);

    return (
        <div className="flex flex-col gap-2 w-full">
            <h1 className="text-sm text-[#1A202C] font-bold">{title}</h1>
            <p className="text-sm font-normal text-[#202020CC]">{paragraph}</p>

            {date && time && (<div className="flex items-center">
                <span className="text-xs font-normal">{formatToMonthDayYear(date)}</span>
                <span className="mx-2" aria-hidden="true">•</span>
                <span className="text-xs font-normal">{formatTime(time)}</span>
            </div>)}
        </div>
    )
}

const TimelineCard = ({ patient }: { patient: PatientTimeline }) => {
    return (
        <div className={cn("rounded-[8px] p-4 flex flex-col gap-4 items-start border w-full shadow-sm cursor-pointer", "bg-[#e3e2e2]")}>
            {patient?.timeline?.length ? (
                patient.timeline.map((item) => {
                    return (
                        <React.Fragment key={`${item.event_type}-${item.encounter_datetime}`}>
                            <div className="flex justify-between w-full gap-4">
                                <div className={cn("flex gap-5 items-start w-full")}>
                                    <Image src="/icons/timeline.svg" alt="timeline" className="w-[25px]" layout="constrained" width={96} height={96} />
                                    <TimelineDetails
                                        title={item.event_type}
                                        paragraph={item.description}
                                        encounter_datetime={item.encounter_datetime}
                                    />
                                </div>
                                {item.event_type === "Refill" && (<div className="flex w-[20%]">
                                    <Button
                                        className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-full text-[#4272DD] rounded-[8px] border-2 border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                    >
                                        Review
                                    </Button>
                                </div>)}

                                {item.event_type === "Appointment" && (<div className="flex w-[20%]">
                                    <Button
                                        className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-full text-[#4272DD] rounded-[8px] border-2 border-[#4272DD] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                    >
                                        View notes
                                    </Button>
                                </div>)}

                            </div>
                        </React.Fragment>
                    );
                })
            ) : (
                <div className="flex flex-col items-center justify-center py-8 px-4">
                    <p className="text-gray-500 text-center">No timeline events available for this patient.</p>
                </div>
            )}
        </div>
    )
}



